// Browser push for the panel's notifications: registers public/push-sw.js and subscribes it with
// the server's VAPID key (GET /panel/notifications/push). Works on Chrome, Edge, Firefox and Android
// Chrome over HTTPS (or localhost); on iPhone only when the panel is added to the Home Screen.
import { getPushConfigApi, subscribePushApi, unsubscribePushApi } from '../api/notificationApi';

const WORKER = '/push-sw.js';

export type PushStatus = 'unsupported' | 'disabled' | 'blocked' | 'off' | 'on';

export const pushSupported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const keyBytes = (base64url: string) => {
  const padded = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const registration = () => navigator.serviceWorker.register(WORKER);

/** Where this browser stands: not possible here, off on the server, blocked by the user, off or on. */
export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';
  const config = await getPushConfigApi().catch(() => null);
  if (!config?.enabled || !config.public_key) return 'disabled';
  if (Notification.permission === 'denied') return 'blocked';
  const reg = await navigator.serviceWorker.getRegistration(WORKER);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub && Notification.permission === 'granted') {
    // Sent again on every check: the server keeps it for this login (one browser, one login)
    await subscribePushApi(sub.toJSON()).catch(() => undefined);
    return 'on';
  }
  return 'off';
}

/** Asks the browser's permission and subscribes; the new status. */
export async function enablePush(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';
  const config = await getPushConfigApi();
  if (!config.enabled || !config.public_key) return 'disabled';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(config.public_key) });
  }
  await subscribePushApi(sub.toJSON());
  return 'on';
}

/** Stops pushes to this browser (e.g. before logging out on a shared computer). */
export async function disablePush(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';
  const reg = await navigator.serviceWorker.getRegistration(WORKER);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await unsubscribePushApi(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe();
  }
  return 'off';
}
